import React, { useState, useEffect } from 'react';

interface ScrollProgressIndicatorProps {
  className?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

export const ScrollProgressIndicator: React.FC<ScrollProgressIndicatorProps> = ({
  className = '',
  position = 'bottom-right',
  size = 'medium',
  showLabel = false,
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      // Calcular el progreso de scroll como porcentaje
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Configurar tamaño basado en prop
  const sizeConfig = {
    small: { width: '48px', height: '48px', strokeWidth: '2' },
    medium: { width: '64px', height: '64px', strokeWidth: '2.5' },
    large: { width: '80px', height: '80px', strokeWidth: '3' },
  };

  const config = sizeConfig[size];

  // Posiciones usando Tailwind (con margen extra para que no choque con React DevTools en desarrollo)
  const positionClasses = {
    'bottom-right': 'bottom-24 right-6 sm:bottom-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6',
  };

  // Radio del círculo (ajustar según el tamaño del SVG)
  const radius = 28; // Para un viewBox de 64x64
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div
      className={`fixed ${positionClasses[position]} z-40 pointer-events-none ${className}`}
      aria-label={`Scroll progress: ${Math.round(progress)}%`}
    >
      {/* SVG Circular Progress */}
      <svg
        width={config.width}
        height={config.height}
        viewBox="0 0 64 64"
        className="transform -rotate-90 drop-shadow-lg"
      >
        {/* Círculo de fondo */}
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="rgba(0, 0, 0, 0.15)"
          strokeWidth={config.strokeWidth}
        />

        {/* Círculo de progreso */}
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="text-gray-800 transition-all duration-100"
        />
      </svg>

      {/* Porcentaje opcional */}
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold">
          {Math.round(progress)}%
        </div>
      )}

      {/* Flecha dentro del círculo cuando no está en top */}
      {progress < 80 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            className="w-4 h-4 text-gray-700 animate-pulse"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      )}
    </div>
  );
};
